import { Button, Field, Form, H4, Text } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';

type SuggestedLayoutControlsProps = {
  panelsCount: number;
  availablePanels: number;
  configurationCount: number;
  configurationIndex: number;
  customLayout: boolean;
  isGenerating: boolean;
  onSelectConfiguration: (index: number) => void;
  onGenerate: () => void;
  onReposition: () => void;
  onDraw: () => void;
};

const SuggestedLayoutControls: FC<SuggestedLayoutControlsProps> = (props) => {
  const {
    panelsCount,
    availablePanels,
    configurationCount,
    configurationIndex,
    customLayout,
    isGenerating,
    onSelectConfiguration,
    onGenerate,
    onReposition,
    onDraw,
  } = props;
  const methods = useForm({ values: { configuration: configurationIndex } });
  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <H4>Suggested layout</H4>
        <Text level="supporting">
          Generate a roof model to edit each roof and avoid obstacles. Analysis
          continues if you leave this page.
        </Text>
        <Button
          variant="secondary"
          size="sm"
          onClick={onGenerate}
          isProcessing={isGenerating}
          disabled={isGenerating}
        >
          <UntitledIcon name="grid-01.3" className="size-4" />
          Generate roof model
        </Button>
      </div>
      <Form methods={methods} onSubmit={() => undefined}>
        <Controller
          name="configuration"
          control={methods.control}
          render={({ field }) => (
            <Field name={field.name} id="solar-designer-config-slider">
              <div className="flex items-center justify-between gap-2">
                <Field.Label>Panels</Field.Label>
                <Text level="supporting">
                  {panelsCount} of {availablePanels}
                  {customLayout ? ' · Custom layout' : ''}
                </Text>
              </div>
              <Field.HelperText>
                Choose a suggested panel count, or click individual panels on
                the map.
              </Field.HelperText>
              <input
                {...field}
                id="solar-designer-config-slider"
                type="range"
                className="w-full cursor-pointer accent-primary"
                min={0}
                max={Math.max(0, configurationCount - 1)}
                step={1}
                disabled={configurationCount < 2}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  field.onChange(value);
                  onSelectConfiguration(value);
                }}
                aria-valuetext={`${panelsCount} panels`}
              />
            </Field>
          )}
        />
      </Form>
      <div className="space-y-2 border-t border-gray-200 pt-4">
        <Text level="supporting">Wrong roof, or nothing found?</Text>
        <Button
          variant="quaternary"
          size="sm"
          onClick={onReposition}
          disabled={isGenerating}
        >
          <UntitledIcon name="marker-pin-01.3" className="size-4" />
          Reposition pin
        </Button>
        <Button
          variant="quaternary"
          size="sm"
          onClick={onDraw}
          disabled={isGenerating}
        >
          <UntitledIcon name="edit-05.3" className="size-4" />
          Draw roof manually
        </Button>
      </div>
    </div>
  );
};

export default SuggestedLayoutControls;
